import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import "@/index.css";
import App from "@/App";
import Admin from "@/Admin";
import WorkbookPro from "@/components/WorkbookPro";
import Dashboard from "@/components/Dashboard";
import FamilyConstellation from "@/components/FamilyConstellation";
import PracticePartners from "@/components/PracticePartners";
import PlanetaryMatch from "@/components/PlanetaryMatch";
import PaymentPage from "@/components/PaymentPage";
import Onboarding from "@/components/Onboarding";
import Login from "@/components/Login";
import Directions from "@/components/Directions";
import LorePage from "@/components/LoreExperience";
import OFNRTrainer from "@/components/OFNRTrainer";
import TeachersGuide from "@/components/TeachersGuide";
import NeedsList from "@/components/workbook/NeedsList";
import FeelingsList from "@/components/workbook/FeelingsList";
import BodySensations from "@/components/workbook/BodySensations";
import YourPresence from "@/components/workbook/YourPresence";
import GoldenRule from "@/components/workbook/GoldenRule";
import NVCTreeOfLife from "@/components/workbook/NVCTreeOfLife";
import FeelingsNeedsExercise from "@/components/workbook/FeelingsNeedsExercise";
import DanceFloorsOne from "@/components/workbook/DanceFloorsOne";
import DanceFloorsTwo from "@/components/workbook/DanceFloorsTwo";
import CompassionateDialog from "@/components/workbook/CompassionateDialog";
import FiveKeyPractices from "@/components/workbook/FiveKeyPractices";
import BasicFeelingsNeeds from "@/components/workbook/BasicFeelingsNeeds";
import NVCComponents from "@/components/workbook/NVCComponents";
import EmpathyIsNot from "@/components/workbook/EmpathyIsNot";
import SelfEmpathy from "@/components/workbook/SelfEmpathy";
import Chapter1Intro from "@/components/workbook/Chapter1Intro";
import Chapter1Presentation from "@/components/workbook/Chapter1Presentation";
import FourBasicChoices from "@/components/workbook/FourBasicChoices";
import FourDsDisconnection from "@/components/workbook/FourDsDisconnection";
import JackalOFNR from "@/components/workbook/JackalOFNR";
import JackalStreetGiraffe from "@/components/workbook/JackalStreetGiraffe";
import Chapter1Homework from "@/components/workbook/Chapter1Homework";
import OFNRStatementsQuestions from "@/components/workbook/OFNRStatementsQuestions";
import OFNRPracticeSheets from "@/components/workbook/OFNRPracticeSheets";
import MutualStrategies from "@/components/workbook/MutualStrategies";
import ConflictShortVersion from "@/components/workbook/ConflictShortVersion";
import ConflictLongVersion from "@/components/workbook/ConflictLongVersion";
import ThreeAreasAttention from "@/components/workbook/ThreeAreasAttention";
import InterpersonalDance from "@/components/workbook/InterpersonalDance";
import InternalMapReality from "@/components/workbook/InternalMapReality";
import CompassionateSuggestions from "@/components/workbook/CompassionateSuggestions";
import CompassionateCommunication from "@/components/workbook/CompassionateCommunication";
import LostLove from "@/components/workbook/LostLove";
import PastInPresent from "@/components/workbook/PastInPresent";
import HomeworkPartnerSchedule from "@/components/workbook/HomeworkPartnerSchedule";
import WellnessInRelationships from "@/components/workbook/WellnessInRelationships";
import PlaceholderPage from "@/components/workbook/PlaceholderPage";
import GratitudeExercises from "@/components/workbook/GratitudeExercises";
import QuadruneBrain from "@/components/workbook/QuadruneBrain";
import GivingReceivingFeedback from "@/components/workbook/GivingReceivingFeedback";
import IMadeItMean from "@/components/workbook/IMadeItMean";
import IntimacyQuestions from "@/components/workbook/IntimacyQuestions";
import CompassionateWheel from "@/components/workbook/CompassionateWheel";
import QuadruneEvolution from "@/components/workbook/QuadruneEvolution";
import NVCKeyIdeas from "@/components/workbook/NVCKeyIdeas";
import NVCPrimer from "@/components/workbook/NVCPrimer";
import ChapterViewer from "@/components/ChapterViewer";
import UserManagementPage from "@/components/UserManagementPage";
import EmergentEmpathy from "@/components/EmergentEmpathy";
import ElchmE from "@/components/ElchmE";
import ElchmELanding from "@/components/ElchmELanding";
import ElchmESketchboard from "@/components/ElchmESketchboard";
import SketchHub from "@/components/SketchHub";
import VisionQuest from "@/components/VisionQuest";
import WisdomLibrary from "@/components/WisdomLibrary";
import ForgotPassword from "@/components/ForgotPassword";
import AcceptInvite from "@/components/AcceptInvite";
import ResearchPortal from "@/components/ResearchPortal";
import UserIndicator from "@/components/UserIndicator";
import { AuthCallback, LogoutPage } from "@/components/AuthPages";
import CompassionateCommunicationChecklist from "@/components/workbook/CompassionateCommunicationChecklist";
import GratitudeCelebrationPractices from "@/components/workbook/GratitudeCelebrationPractices";
import ConnectionRoadmap from "@/components/workbook/ConnectionRoadmap";
import PositiveExperiencesStick from "@/components/workbook/PositiveExperiencesStick";
import FearOfFeelings from "@/components/workbook/FearOfFeelings";
import RiskDreadedExperience from "@/components/workbook/RiskDreadedExperience";
import SayWhatYouMean from "@/components/workbook/SayWhatYouMean";
import { Toaster } from "@/components/ui/sonner";
import GlobalNoteTakingPad from "@/components/GlobalNoteTakingPad";
import MobileEbook from "@/components/MobileEbook";

// ScrollToTop component - scrolls to top on every navigation
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ScrollToTop />
      <GlobalNoteTakingPad />
      <Routes>
        {/* ElchmE is now the primary landing page */}
        <Route path="/" element={<ElchmELanding />} />
        <Route path="/belonging-sessions" element={<App />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<Login />} />
        <Route path="/workbook" element={<WorkbookPro />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/lore" element={<LorePage />} />
        <Route path="/ofnr-trainer" element={<OFNRTrainer />} />
        <Route path="/belonging-ebook" element={<TeachersGuide />} />
        <Route path="/belonging-ebook/mobile" element={<MobileEbook />} />
        <Route path="/ebook" element={<MobileEbook />} />
        <Route path="/teachers-guide" element={<Navigate to="/belonging-ebook" replace />} />
        <Route path="/workbook/needs-list" element={<NeedsList />} />
        <Route path="/workbook/feelings-list" element={<FeelingsList />} />
        <Route path="/workbook/body-sensations" element={<BodySensations />} />
        <Route path="/workbook/your-presence" element={<YourPresence />} />
        <Route path="/workbook/golden-rule" element={<GoldenRule />} />
        <Route path="/workbook/nvc-tree-of-life" element={<NVCTreeOfLife />} />
        <Route path="/workbook/dance-floors-one" element={<DanceFloorsOne />} />
        <Route path="/workbook/dance-floors-two" element={<DanceFloorsTwo />} />
        <Route path="/workbook/compassionate-dialog" element={<CompassionateDialog />} />
        <Route path="/workbook/five-key-practices" element={<FiveKeyPractices />} />
        <Route path="/workbook/basic-feelings-needs" element={<BasicFeelingsNeeds />} />
        <Route path="/workbook/feelings-needs-exercise" element={<FeelingsNeedsExercise />} />
        <Route path="/workbook/nvc-components" element={<NVCComponents />} />
        <Route path="/workbook/empathy-is-not" element={<EmpathyIsNot />} />
        <Route path="/workbook/self-empathy" element={<SelfEmpathy />} />
        <Route path="/workbook/chapter-1-intro" element={<Chapter1Intro />} />
        <Route path="/workbook/chapter-1" element={<Chapter1Presentation />} />
        <Route path="/workbook/four-basic-choices" element={<FourBasicChoices />} />
        <Route path="/workbook/four-ds-disconnection" element={<FourDsDisconnection />} />
        <Route path="/workbook/jackal-ofnr" element={<JackalOFNR />} />
        <Route path="/workbook/jackal-street-giraffe" element={<JackalStreetGiraffe />} />
        <Route path="/workbook/chapter-1-homework" element={<Chapter1Homework />} />
        <Route path="/workbook/ofnr-statements-questions" element={<OFNRStatementsQuestions />} />
        <Route path="/workbook/ofnr-practice-sheets" element={<OFNRPracticeSheets />} />
        <Route path="/workbook/mutual-strategies" element={<MutualStrategies />} />
        <Route path="/workbook/conflict-short" element={<ConflictShortVersion />} />
        <Route path="/workbook/conflict-long" element={<ConflictLongVersion />} />
        <Route path="/workbook/three-areas-attention" element={<ThreeAreasAttention />} />
        <Route path="/workbook/interpersonal-dance" element={<InterpersonalDance />} />
        <Route path="/workbook/internal-map-reality" element={<InternalMapReality />} />
        <Route path="/workbook/compassionate-suggestions" element={<CompassionateSuggestions />} />
        <Route path="/workbook/compassionate-communication" element={<CompassionateCommunication />} />
        <Route path="/workbook/lost-love" element={<LostLove />} />
        <Route path="/workbook/past-in-present" element={<PastInPresent />} />
        <Route path="/workbook/homework-partner-schedule" element={<HomeworkPartnerSchedule />} />
        <Route path="/workbook/wellness-in-relationships" element={<WellnessInRelationships />} />
        <Route path="/workbook/gratitude-exercises" element={<GratitudeExercises />} />
        <Route path="/workbook/quadrune-brain" element={<QuadruneBrain />} />
        <Route path="/workbook/giving-receiving-feedback" element={<GivingReceivingFeedback />} />
        <Route path="/workbook/i-made-it-mean" element={<IMadeItMean />} />
        <Route path="/workbook/intimacy-questions" element={<IntimacyQuestions />} />
        <Route path="/workbook/compassionate-wheel" element={<CompassionateWheel />} />
        <Route path="/workbook/page-37" element={<CompassionateCommunicationChecklist />} />
        <Route path="/workbook/page-40" element={<GratitudeCelebrationPractices />} />
        <Route path="/workbook/page-45" element={<ConnectionRoadmap />} />
        <Route path="/workbook/page-55" element={<PositiveExperiencesStick />} />
        <Route path="/workbook/page-57" element={<FearOfFeelings />} />
        <Route path="/workbook/page-59" element={<RiskDreadedExperience />} />
        <Route path="/workbook/quadrune-evolution" element={<QuadruneEvolution />} />
        <Route path="/workbook/nvc-key-ideas" element={<NVCKeyIdeas />} />
        <Route path="/workbook/nvc-primer" element={<NVCPrimer />} />
        <Route path="/workbook/say-what-you-mean" element={<SayWhatYouMean />} />
        <Route path="/workbook/chapter" element={<ChapterViewer />} />
        <Route path="/admin/user/:userId" element={<UserManagementPage />} />
        <Route path="/emergent-empathy" element={<EmergentEmpathy />} />
        <Route path="/elchme" element={<ElchmE />} />
        <Route path="/ElchmE" element={<ElchmE />} />
        <Route path="/alchemy" element={<ElchmE />} />
        <Route path="/elchme/flow" element={<ElchmE />} />
        <Route path="/elchme/sketchboard" element={<ElchmESketchboard />} />
        <Route path="/elchme/sketchboard/:boardId" element={<ElchmESketchboard />} />
        <Route path="/sketch" element={<SketchHub />} />
        <Route path="/sketch/:projectId" element={<SketchHub />} />
        <Route path="/sketch-hub" element={<SketchHub />} />
        <Route path="/sketch-hub/:projectId" element={<SketchHub />} />
        <Route path="/vision-quest" element={<VisionQuest />} />
        <Route path="/vision" element={<VisionQuest />} />
        <Route path="/constellation" element={<FamilyConstellation />} />
        <Route path="/partners" element={<PracticePartners />} />
        <Route path="/planetary" element={<PlanetaryMatch />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment-success" element={<PaymentPage />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/directions" element={<Directions />} />
        <Route path="/wisdom" element={<Navigate to="/lore" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/research" element={<ResearchPortal />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/sso" element={<AuthCallback />} />
        <Route path="/auth/logout" element={<LogoutPage />} />
      </Routes>
      <UserIndicator />
      <Toaster />
    </BrowserRouter>
  </React.StrictMode>,
);
